import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
    ArrowLeft, Shield, Key, Building, Users, Upload, RefreshCw,
    CheckCircle, XCircle, Copy, Download, Trash2, Plus, Search,
    Calendar, Clock, FileText, AlertTriangle, Settings, LogOut, RotateCcw, Mail, Bell
} from 'lucide-react';
import InstitutionalManagement from '../components/admin/InstitutionalManagement';
import OrganizationsManagement from '../components/admin/OrganizationsManagement';
import SubscriptionCodesTable from '../components/admin/SubscriptionCodesTable';
import GenerateCodeForm from '../components/admin/GenerateCodeForm';
import ComplianceDocumentsTab from '../components/admin/ComplianceDocumentsTab';
import SecurityTab from '../components/admin/SecurityTab';
import HazardLogTab from '../components/admin/HazardLogTab';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminPage() {
    const navigate = useNavigate();
    const { authToken, user, logout } = useAuth();
    const [codes, setCodes] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    
    // Stats
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        expired: 0,
        revoked: 0,
        usic: 0,
        csic: 0
    });
    
    // New code form
    const [newCode, setNewCode] = useState({
        code_type: 'USIC',
        organization_name: '',
        user_email: '',
        parent_csic: '',
        expires_days: 365,
        notes: ''
    });

    // Bulk import
    const [bulkFile, setBulkFile] = useState(null);
    const [importing, setImporting] = useState(false);

    // Restore dialog
    const [restoreDialog, setRestoreDialog] = useState({ open: false, code: null });
    const [extendDays, setExtendDays] = useState(365);
    const [restoring, setRestoring] = useState(false);

    // Compliance documents
    const [docCategories, setDocCategories] = useState([]);
    const [docsLoading, setDocsLoading] = useState(false);
    const [previewDoc, setPreviewDoc] = useState({ open: false, title: '', content: '', loading: false });

    // Signatories
    const [signatories, setSignatories] = useState({
        owner: '', dpo: '', caldicott: '', cso: '', siro: '',
    });
    const [signatoriesOpen, setSignatoriesOpen] = useState(false);
    const [savingSignatories, setSavingSignatories] = useState(false);

    const headers = { Authorization: `Bearer ${authToken}` };

    useEffect(() => {
        fetchCodes();
        fetchInstitutions();
        fetchComplianceDocuments();
        fetchSignatories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken]);

    const fetchSignatories = async () => {
        try {
            const r = await axios.get(`${API}/admin/compliance-signatories`, { headers });
            setSignatories(r.data || {});
        } catch (error) {
            console.error('Failed to load signatories:', error);
        }
    };

    const saveSignatories = async () => {
        setSavingSignatories(true);
        try {
            await axios.put(`${API}/admin/compliance-signatories`, signatories, { headers });
            toast.success('Signatories saved. New PDFs will pre-fill these names.');
            setSignatoriesOpen(false);
        } catch (e) {
            toast.error('Failed to save signatories');
        } finally {
            setSavingSignatories(false);
        }
    };

    const fetchComplianceDocuments = async () => {
        setDocsLoading(true);
        try {
            const response = await axios.get(`${API}/admin/compliance-documents`, { headers });
            setDocCategories(response.data || []);
        } catch (error) {
            console.error('Error fetching documents:', error);
        } finally {
            setDocsLoading(false);
        }
    };

    const downloadDocument = async (category, filename) => {
        try {
            const response = await axios.get(
                `${API}/admin/compliance-documents/${category}/${filename}`,
                { headers, responseType: 'blob' }
            );
            const blob = new Blob([response.data], { type: 'text/markdown;charset=utf-8' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Downloaded ${filename}`);
        } catch (error) {
            console.error('Download error:', error);
            toast.error('Failed to download document');
        }
    };
    // Kept for potential external callers / future re-use. The Documents tab
    // now handles its own download via ComplianceDocumentsTab.
    void downloadDocument;

    // Kept here because the parent owns the preview dialog state
    const previewDocument = async (category, filename, title) => {
        setPreviewDoc({ open: true, title, content: '', loading: true });
        try {
            const response = await axios.get(
                `${API}/admin/compliance-documents/${category}/${filename}?inline=true`,
                { headers, responseType: 'text' }
            );
            setPreviewDoc({ open: true, title, content: response.data, loading: false });
        } catch (error) {
            console.error('Preview error:', error);
            toast.error('Failed to load preview');
            setPreviewDoc({ open: false, title: '', content: '', loading: false });
        }
    };

    const fetchCodes = async () => {
        try {
            const response = await axios.get(`${API}/subscription/codes`, { headers });
            const codesData = response.data || [];
            setCodes(codesData);
            
            // Calculate stats
            const now = new Date();
            const active = codesData.filter(c => c.is_active && (!c.expires_at || new Date(c.expires_at) > now)).length;
            const expired = codesData.filter(c => c.expires_at && new Date(c.expires_at) <= now).length;
            const revoked = codesData.filter(c => !c.is_active).length;
            const usic = codesData.filter(c => c.code_type === 'USIC').length;
            const csic = codesData.filter(c => c.code_type === 'CSIC').length;
            
            setStats({
                total: codesData.length,
                active,
                expired,
                revoked,
                usic,
                csic
            });
        } catch (error) {
            console.error('Error fetching codes:', error);
            toast.error('Failed to load subscription codes');
        } finally {
            setLoading(false);
        }
    };

    const fetchInstitutions = async () => {
        try {
            const response = await axios.get(`${API}/institutions`, { headers });
            setInstitutions(response.data || []);
        } catch (error) {
            console.error('Error fetching institutions:', error);
        }
    };

    const generateCode = async () => {
        if (newCode.code_type === 'CSIC' && !newCode.organization_name) {
            toast.error('Organisation name is required for CSIC');
            return;
        }
        if (newCode.code_type === 'USIC' && !newCode.parent_csic) {
            toast.error('Please select the organisation (CSIC) this USIC belongs to');
            return;
        }

        setGenerating(true);
        try {
            const payload = {
                code_type: newCode.code_type,
                organization_name: newCode.organization_name || undefined,
                parent_csic: newCode.code_type === 'USIC' ? newCode.parent_csic : undefined,
                user_email: newCode.user_email || undefined,
                expires_days: parseInt(newCode.expires_days),
            };
            const response = await axios.post(`${API}/subscription/generate`, payload, { headers });

            toast.success(`Code generated: ${response.data.code}`);
            fetchCodes();
            setNewCode({
                code_type: 'USIC',
                organization_name: '',
                user_email: '',
                parent_csic: '',
                expires_days: 365,
                notes: '',
            });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to generate code');
        } finally {
            setGenerating(false);
        }
    };

    const revokeCode = async (codeId) => {
        if (!window.confirm('Are you sure you want to revoke this code?')) return;
        
        try {
            await axios.put(`${API}/subscription/codes/${codeId}/revoke`, {}, { headers });
            toast.success('Code revoked successfully');
            fetchCodes();
        } catch (error) {
            toast.error('Failed to revoke code');
        }
    };

    const copyCode = (code) => {
        navigator.clipboard.writeText(code);
        toast.success('Code copied to clipboard');
    };

    const handleBulkImport = async () => {
        if (!bulkFile) {
            toast.error('Please select a CSV file');
            return;
        }
        
        setImporting(true);
        try {
            const formData = new FormData();
            formData.append('file', bulkFile);
            
            const response = await axios.post(`${API}/subscription/bulk-import`, formData, {
                headers: { ...headers, 'Content-Type': 'multipart/form-data' }
            });
            
            toast.success(`Imported ${response.data.imported} codes successfully`);
            fetchCodes();
            setBulkFile(null);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Import failed');
        } finally {
            setImporting(false);
        }
    };

    const exportCodes = () => {
        const csvContent = [
            ['Code', 'Type', 'Organization', 'Status', 'Created', 'Expires', 'Used By', 'Used By Email'].join(','),
            ...codes.map(c => [
                c.code,
                c.code_type,
                c.organization_name || '',
                c.is_active ? 'Active' : 'Revoked',
                c.created_at?.split('T')[0] || '',
                c.expires_at?.split('T')[0] || 'Never',
                c.used_by || '',
                c.used_by_email || ''
            ].join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `subscription_codes_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        toast.success('Codes exported');
    };

    const restoreCode = async () => {
        if (!restoreDialog.code) return;
        
        setRestoring(true);
        try {
            await axios.put(
                `${API}/subscription/codes/${restoreDialog.code.id}/restore?extend_days=${extendDays}`,
                {},
                { headers }
            );
            toast.success(`Code restored successfully! Extended by ${extendDays} days.`);
            fetchCodes();
            setRestoreDialog({ open: false, code: null });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to restore code');
        } finally {
            setRestoring(false);
        }
    };

    const triggerExpiredCheck = async () => {
        try {
            await axios.post(`${API}/subscription/check-expired`, {}, { headers });
            toast.success('Expired subscription check completed');
            fetchCodes();
        } catch (error) {
            toast.error('Failed to run expired check');
        }
    };

    const triggerRenewalReminders = async () => {
        try {
            await axios.post(`${API}/subscription/send-reminders`, {}, { headers });
            toast.success('Renewal reminder emails sent');
        } catch (error) {
            toast.error('Failed to send reminders');
        }
    };

    // Filter codes
    const filteredCodes = codes.filter(code => {
        const matchesSearch = !searchTerm || 
            code.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            code.organization_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            code.used_by?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            code.used_by_email?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesType = filterType === 'all' || code.code_type === filterType;
        
        const now = new Date();
        const isExpired = code.expires_at && new Date(code.expires_at) <= now;
        const matchesStatus = filterStatus === 'all' ||
            (filterStatus === 'active' && code.is_active && !isExpired) ||
            (filterStatus === 'expired' && isExpired) ||
            (filterStatus === 'revoked' && !code.is_active);
        
        return matchesSearch && matchesType && matchesStatus;
    });

    const getStatusBadge = (code) => {
        const now = new Date();
        const isExpired = code.expires_at && new Date(code.expires_at) <= now;
        
        if (!code.is_active) {
            return <Badge variant="destructive">Revoked</Badge>;
        }
        if (isExpired) {
            return <Badge variant="secondary" className="bg-amber-100 text-amber-800">Expired</Badge>;
        }
        return <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <RefreshCw className="w-8 h-8 animate-spin text-teal-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50" data-testid="admin-page">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <Shield className="w-5 h-5 text-teal-600" />
                            <span className="font-semibold text-slate-900">FendanaCor Admin Portal</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-500 mr-2">
                                {user?.full_name || user?.email}
                            </span>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={triggerExpiredCheck}
                                title="Check for expired subscriptions"
                            >
                                <Clock className="w-4 h-4 mr-2" />
                                Check Expired
                            </Button>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={triggerRenewalReminders}
                                title="Send renewal reminder emails"
                            >
                                <Mail className="w-4 h-4 mr-2" />
                                Send Reminders
                            </Button>
                            <Button variant="outline" size="sm" onClick={exportCodes}>
                                <Download className="w-4 h-4 mr-2" />
                                Export
                            </Button>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => {
                                    logout();
                                    navigate('/');
                                    toast.success('Signed out successfully');
                                }}
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                data-testid="admin-logout-btn"
                            >
                                <LogOut className="w-4 h-4 mr-2" />
                                Sign Out
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats Cards */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8"
                >
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                            <p className="text-xs text-slate-500">Total Codes</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-emerald-600">{stats.active}</p>
                            <p className="text-xs text-slate-500">Active</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-amber-600">{stats.expired}</p>
                            <p className="text-xs text-slate-500">Expired</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-red-600">{stats.revoked}</p>
                            <p className="text-xs text-slate-500">Revoked</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-blue-600">{stats.usic}</p>
                            <p className="text-xs text-slate-500">USIC</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-purple-600">{stats.csic}</p>
                            <p className="text-xs text-slate-500">CSIC</p>
                        </CardContent>
                    </Card>
                </motion.div>

                <Tabs defaultValue="codes" className="space-y-6">
                    <TabsList className="grid w-full max-w-6xl grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-1 h-auto">
                        <TabsTrigger value="codes" data-testid="tab-codes">Subscription Codes</TabsTrigger>
                        <TabsTrigger value="organizations" data-testid="tab-organizations">Organisations</TabsTrigger>
                        <TabsTrigger value="institutions" data-testid="tab-institutions">Institutions</TabsTrigger>
                        <TabsTrigger value="generate" data-testid="tab-generate">Generate</TabsTrigger>
                        <TabsTrigger value="import" data-testid="tab-import">Bulk Import</TabsTrigger>
                        <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
                        <TabsTrigger value="hazard-log" data-testid="tab-hazard-log">Hazard Log</TabsTrigger>
                        <TabsTrigger value="security" data-testid="tab-security">Profile &amp; Security</TabsTrigger>
                    </TabsList>

                    {/* Institutions Hierarchy Tab */}
                    <TabsContent value="institutions">
                        <InstitutionalManagement authToken={authToken} />
                    </TabsContent>

                    <TabsContent value="hazard-log">
                        <HazardLogTab />
                    </TabsContent>

                    <TabsContent value="organizations">
                        <OrganizationsManagement authToken={authToken} />
                    </TabsContent>

                    {/* Codes List Tab */}
                    <TabsContent value="codes">
                        <SubscriptionCodesTable
                            codes={filteredCodes}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            filterType={filterType}
                            setFilterType={setFilterType}
                            filterStatus={filterStatus}
                            setFilterStatus={setFilterStatus}
                            getStatusBadge={getStatusBadge}
                            copyCode={copyCode}
                            revokeCode={revokeCode}
                            onRestoreClick={(code) => setRestoreDialog({ open: true, code })}
                        />
                    </TabsContent>

                    {/* Generate Tab */}
                    <TabsContent value="generate">
                        <GenerateCodeForm
                            newCode={newCode}
                            setNewCode={setNewCode}
                            codes={codes}
                            generating={generating}
                            onGenerate={generateCode}
                        />
                    </TabsContent>

                    {/* Bulk Import Tab */}
                    <TabsContent value="import">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Upload className="w-5 h-5 text-teal-600" />
                                    Bulk Import
                                </CardTitle>
                                <CardDescription>
                                    Import multiple subscription codes from a CSV file
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-6 max-w-md">
                                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                                        <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                                        <p className="text-sm text-slate-600 mb-4">
                                            Upload a CSV file with columns: code_type, company_name, expires_days
                                        </p>
                                        <input
                                            type="file"
                                            accept=".csv"
                                            onChange={(e) => setBulkFile(e.target.files?.[0])}
                                            className="hidden"
                                            id="csv-upload"
                                        />
                                        <label htmlFor="csv-upload">
                                            <Button variant="outline" asChild>
                                                <span>Select CSV File</span>
                                            </Button>
                                        </label>
                                        {bulkFile && (
                                            <p className="mt-4 text-sm text-teal-600">
                                                Selected: {bulkFile.name}
                                            </p>
                                        )}
                                    </div>

                                    <div className="bg-slate-50 p-4 rounded-lg">
                                        <h4 className="font-medium text-slate-700 mb-2">CSV Format Example:</h4>
                                        <code className="text-xs text-slate-600 block">
                                            code_type,company_name,expires_days<br/>
                                            USIC,,365<br/>
                                            CSIC,NHS Trust London,365<br/>
                                            CSIC,Royal Hospital,180
                                        </code>
                                    </div>

                                    <Button 
                                        onClick={handleBulkImport} 
                                        disabled={!bulkFile || importing}
                                        className="w-full bg-teal-600 hover:bg-teal-700"
                                    >
                                        {importing ? (
                                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Upload className="w-4 h-4 mr-2" />
                                        )}
                                        Import Codes
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Compliance Documents Tab */}
                    <TabsContent value="documents">
                        <ComplianceDocumentsTab
                            authToken={authToken}
                            docCategories={docCategories}
                            docsLoading={docsLoading}
                            previewDocument={previewDocument}
                            onConfigureSignatories={() => setSignatoriesOpen(true)}
                        />
                    </TabsContent>

                    {/* Profile & Security Tab (MFA setup) */}
                    <TabsContent value="security">
                        <SecurityTab authToken={authToken} user={user} />
                    </TabsContent>
                </Tabs>
            </main>

            {/* Restore Code Dialog */}
            <Dialog open={restoreDialog.open} onOpenChange={(open) => setRestoreDialog({ open, code: open ? restoreDialog.code : null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <RotateCcw className="w-5 h-5 text-emerald-600" />
                            Restore Subscription Code
                        </DialogTitle>
                        <DialogDescription>
                            Restore this revoked code and optionally extend its validity period.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {restoreDialog.code && (
                        <div className="space-y-4 py-4">
                            <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                                <p className="text-sm">
                                    <span className="font-medium">Code:</span>{' '}
                                    <code className="bg-slate-200 px-2 py-0.5 rounded">{restoreDialog.code.code}</code>
                                </p>
                                <p className="text-sm">
                                    <span className="font-medium">Type:</span> {restoreDialog.code.code_type}
                                </p>
                                <p className="text-sm">
                                    <span className="font-medium">Organization:</span> {restoreDialog.code.organization_name || '-'}
                                </p>
                                <p className="text-sm">
                                    <span className="font-medium">Used By:</span> {restoreDialog.code.used_by || '-'}
                                    {restoreDialog.code.used_by_email && (
                                        <span className="text-slate-500"> ({restoreDialog.code.used_by_email})</span>
                                    )}
                                </p>
                                <p className="text-sm">
                                    <span className="font-medium">Original Expiry:</span>{' '}
                                    {restoreDialog.code.expires_at 
                                        ? new Date(restoreDialog.code.expires_at).toLocaleDateString() 
                                        : 'Never'}
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label>Extend Validity (Days)</Label>
                                <Select 
                                    value={extendDays.toString()} 
                                    onValueChange={(v) => setExtendDays(parseInt(v))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="0">No extension (restore only)</SelectItem>
                                        <SelectItem value="30">30 days</SelectItem>
                                        <SelectItem value="90">90 days</SelectItem>
                                        <SelectItem value="180">180 days (6 months)</SelectItem>
                                        <SelectItem value="365">365 days (1 year)</SelectItem>
                                        <SelectItem value="730">730 days (2 years)</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-slate-500">
                                    If the code has expired, the extension starts from today. Otherwise, it extends from the current expiry date.
                                </p>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setRestoreDialog({ open: false, code: null })}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={restoreCode}
                            disabled={restoring}
                            className="bg-emerald-600 hover:bg-emerald-700"
                        >
                            {restoring ? (
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <RotateCcw className="w-4 h-4 mr-2" />
                            )}
                            Restore Code
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Compliance Signatories Dialog */}
            <Dialog open={signatoriesOpen} onOpenChange={setSignatoriesOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Settings className="w-5 h-5 text-teal-600" />
                            Configure Sign-off Names
                        </DialogTitle>
                        <DialogDescription>
                            These names will be pre-filled on the Approval &amp; Sign-Off page of every generated PDF. Leave blank to keep a manual signature line.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        {[
                            { key: 'owner', label: 'Document Owner / Author' },
                            { key: 'dpo', label: 'Data Protection Officer (DPO)' },
                            { key: 'caldicott', label: 'Caldicott Guardian' },
                            { key: 'cso', label: 'Clinical Safety Officer (CSO)' },
                            { key: 'siro', label: 'Senior Information Risk Owner (SIRO)' },
                        ].map(({ key, label }) => (
                            <div key={key} className="space-y-1.5">
                                <Label htmlFor={`sig-${key}`} className="text-sm">{label}</Label>
                                <Input
                                    id={`sig-${key}`}
                                    data-testid={`sig-input-${key}`}
                                    value={signatories[key] || ''}
                                    onChange={(e) => setSignatories({ ...signatories, [key]: e.target.value })}
                                    placeholder="Full name (e.g. Dr. Sarah Patel)"
                                />
                            </div>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSignatoriesOpen(false)}>Cancel</Button>
                        <Button
                            onClick={saveSignatories}
                            disabled={savingSignatories}
                            data-testid="save-signatories-btn"
                            className="bg-teal-600 hover:bg-teal-700"
                        >
                            {savingSignatories ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                            Save Signatories
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Document Preview Dialog */}
            <Dialog
                open={previewDoc.open}
                onOpenChange={(open) => setPreviewDoc({ ...previewDoc, open })}
            >
                <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-teal-600" />
                            {previewDoc.title}
                        </DialogTitle>
                        <DialogDescription>Markdown preview · scroll to view full content</DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-900 rounded-md p-4 border border-slate-200 dark:border-slate-700">
                        {previewDoc.loading ? (
                            <p className="text-sm text-slate-500">Loading...</p>
                        ) : (
                            <pre className="text-xs whitespace-pre-wrap font-mono text-slate-800 dark:text-slate-200" data-testid="doc-preview-content">
                                {previewDoc.content}
                            </pre>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
